import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import {
  Video,
  TrendingUp,
  Users,
  Calendar,
  Sparkles,
  Menu,
  X,
  LogIn,
  UsersRound,
  LogOut,
  Shield,
  User,
  ChevronDown,
  Radio,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAuthenticated, isAdmin, logout } = useAuth();

  const navLinks = [
    { path: '/events', label: 'Events', icon: Calendar },
    { path: '/athletes', label: 'Athletes', icon: Users },
    // Hidden from nav (coming soon): { path: '/teams', label: 'Rooam Teams', icon: UsersRound },
    // Hidden from nav: { path: '/dashboard', label: 'Dashboard', icon: TrendingUp },
    // Hidden from nav: { path: '/admin', label: 'Admin', icon: Shield },
  ];

  const isActive = (path) => location.pathname === path;

  // Close mobile menu on route change
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
    navigate('/');
    setIsOpen(false);
  };

  return (
    <nav className="bg-rooam-black border-b border-rooam-gray-light sticky top-0 z-50 shadow-lg">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <img 
              src="/rooam-logo.png" 
              alt="Rooam Sports Network" 
              className="h-14 sm:h-16 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-1">
            {navLinks
              .map((link) => {
              const Icon = link.icon;
              return (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`flex items-center space-x-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    isActive(link.path)
                      ? 'bg-rooam-blue bg-opacity-10 text-rooam-blue'
                      : 'text-gray-300 hover:bg-rooam-gray-light hover:text-white'
                  } ${link.path === '/admin' ? 'text-rooam-red' : ''}`}
                >
                  <Icon className="h-4 w-4" />
                  <span>{link.label}</span>
                </Link>
              );
            })}
          </div>

          {/* Right side buttons */}
          <div className="hidden md:flex items-center space-x-3">
            {isAuthenticated() ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="outline" 
                    className={`border-rooam-gray-light text-gray-300 hover:bg-rooam-gray-light hover:text-white ${
                      isAdmin() ? 'border-rooam-red/50' : ''
                    }`}
                    data-testid="user-menu-trigger"
                  >
                    <div className="flex items-center gap-2">
                      {isAdmin() ? (
                        <Shield className="h-4 w-4 text-rooam-red" />
                      ) : (
                        <User className="h-4 w-4" />
                      )}
                      <span className="max-w-[120px] truncate">{user?.name}</span>
                      <ChevronDown className="h-4 w-4" />
                    </div>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="bg-rooam-gray-medium border-rooam-gray-light" align="end">
                  <DropdownMenuLabel className="text-gray-300">
                    <div className="flex flex-col">
                      <span className="font-semibold">{user?.name}</span>
                      <span className="text-xs text-gray-500">{user?.email}</span>
                      <span className={`text-xs mt-1 ${isAdmin() ? 'text-rooam-red' : 'text-rooam-blue'}`}>
                        {user?.role?.charAt(0).toUpperCase() + user?.role?.slice(1)}
                      </span>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator className="bg-rooam-gray-light" />
                  
                  <DropdownMenuItem 
                    className="text-gray-300 hover:bg-rooam-gray-light cursor-pointer"
                    onClick={() => navigate('/account')}
                    data-testid="my-account-link"
                  >
                    <User className="h-4 w-4 mr-2 text-cyan-400" />
                    My Account
                  </DropdownMenuItem>

                  {/* Role-based menu items - staff only */}
                  {user?.role === 'operator' ? (
                    <>
                      <DropdownMenuItem 
                        className="text-gray-300 hover:bg-rooam-gray-light cursor-pointer"
                        onClick={() => navigate('/operator')}
                      >
                        <Radio className="h-4 w-4 mr-2 text-red-400" />
                        Operator Console
                      </DropdownMenuItem>
                    </>
                  ) : ['admin', 'super_admin', 'team_admin'].includes(user?.role) ? (
                    <>
                      <DropdownMenuItem 
                        className="text-gray-300 hover:bg-rooam-gray-light cursor-pointer"
                        onClick={() => navigate('/dashboard')}
                      >
                        <TrendingUp className="h-4 w-4 mr-2" />
                        Dashboard
                      </DropdownMenuItem>
                    </>
                  ) : null}
                  
                  {isAdmin() && (
                    <>
                      <DropdownMenuItem 
                        className="text-gray-300 hover:bg-rooam-gray-light cursor-pointer"
                        onClick={() => navigate('/admin')}
                      >
                        <Shield className="h-4 w-4 mr-2 text-rooam-red" />
                        Admin Panel
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        className="text-gray-300 hover:bg-rooam-gray-light cursor-pointer"
                        onClick={() => navigate('/storage')}
                      >
                        <Shield className="h-4 w-4 mr-2 text-rooam-red" />
                        Game Storage
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuSeparator className="bg-rooam-gray-light" />
                  <DropdownMenuItem 
                    className="text-red-400 hover:bg-rooam-gray-light cursor-pointer"
                    onClick={handleLogout}
                    data-testid="logout-button"
                  >
                    <LogOut className="h-4 w-4 mr-2" />
                    Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <>
                <Button variant="outline" size="sm" className="border-rooam-gray-light text-gray-300 hover:bg-rooam-gray-light hover:text-white" asChild>
                  <Link to="/login" data-testid="nav-login-button">
                    <LogIn className="h-4 w-4 mr-2" />
                    Sign In
                  </Link>
                </Button>
                <Button size="sm" className="bg-rooam-blue hover:bg-rooam-blue-light text-black font-semibold" asChild>
                  <Link to="/register" data-testid="nav-register-button">Get Started</Link>
                </Button>
              </>
            )}
          </div>

          {/* Mobile menu button */}
          <button
            className="md:hidden p-2 rounded-lg text-gray-300 hover:bg-rooam-gray-light"
            onClick={() => setIsOpen(!isOpen)}
          >
            {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {isOpen && (
        <div className="md:hidden border-t border-rooam-gray-light bg-rooam-black">
          <div className="px-4 py-3 space-y-1">
            {navLinks
              .map((link) => {
              const Icon = link.icon;
              return (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`flex items-center space-x-2 px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                    isActive(link.path)
                      ? 'bg-rooam-blue bg-opacity-10 text-rooam-blue'
                      : 'text-gray-300 hover:bg-rooam-gray-light hover:text-white'
                  } ${link.path === '/admin' ? 'text-rooam-red' : ''}`}
                  onClick={() => setIsOpen(false)}
                >
                  <Icon className="h-5 w-5" />
                  <span>{link.label}</span>
                </Link>
              );
            })}
            <div className="pt-3 space-y-2">
              {isAuthenticated() ? (
                <>
                  <div className="px-4 py-2 border-t border-rooam-gray-light">
                    <div className="flex items-center gap-2 text-gray-300">
                      {isAdmin() ? (
                        <Shield className="h-4 w-4 text-rooam-red" />
                      ) : (
                        <User className="h-4 w-4" />
                      )}
                      <span className="font-medium">{user?.name}</span>
                    </div>
                    <span className={`text-xs ${isAdmin() ? 'text-rooam-red' : 'text-rooam-blue'}`}>
                      {user?.role?.charAt(0).toUpperCase() + user?.role?.slice(1)}
                    </span>
                  </div>
                  <Link
                    to="/account"
                    className="flex items-center space-x-2 px-4 py-3 rounded-lg text-sm font-medium text-gray-300 hover:bg-rooam-gray-light"
                    onClick={() => setIsOpen(false)}
                    data-testid="mobile-my-account-link"
                  >
                    <User className="h-5 w-5 text-cyan-400" />
                    <span>My Account</span>
                  </Link>
                  <Link
                    to="/storage"
                    className="flex items-center space-x-2 px-4 py-3 rounded-lg text-sm font-medium text-gray-300 hover:bg-rooam-gray-light"
                    onClick={() => setIsOpen(false)}
                  >
                    <Shield className="h-5 w-5 text-rooam-red" />
                    <span>Game Storage</span>
                  </Link>
                  <Button 
                    variant="outline" 
                    className="w-full border-red-500/50 text-red-400 hover:bg-red-500/10"
                    onClick={handleLogout}
                  >
                    <LogOut className="h-4 w-4 mr-2" />
                    Sign Out
                  </Button>
                </>
              ) : (
                <>
                  <Button variant="outline" className="w-full border-rooam-gray-light text-gray-300 hover:bg-rooam-gray-light" asChild>
                    <Link to="/login" onClick={() => setIsOpen(false)}>
                      <LogIn className="h-4 w-4 mr-2" />
                      Sign In
                    </Link>
                  </Button>
                  <Button className="w-full bg-rooam-blue hover:bg-rooam-blue-light text-black font-semibold" asChild>
                    <Link to="/register" onClick={() => setIsOpen(false)}>
                      Get Started
                    </Link>
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
